import { Link } from "react-router-dom";
import {
  ArrowUpRight, FileText, Landmark, FolderOpen, Users, Receipt, Inbox, Briefcase, LineChart,
} from "lucide-react";
import { MaskedLines, FadeUp, Overline } from "@/components/Reveal";
import PageHeader from "@/components/PageHeader";
import CtaBand from "@/components/CtaBand";

const FACTORS = [
  { icon: FileText, label: "Monthly transaction volume" },
  { icon: Landmark, label: "Number of bank and credit card accounts" },
  { icon: FolderOpen, label: "Condition of the existing books" },
  { icon: Users, label: "Payroll" },
  { icon: Receipt, label: "Sales tax requirements" },
  { icon: Inbox, label: "Accounts receivable" },
  { icon: Briefcase, label: "Job costing or project tracking" },
  { icon: LineChart, label: "Complexity of financial reporting" },
];

const QUESTIONS = [
  "Can I afford another employee?",
  "Is this job actually profitable?",
  "Can I purchase new equipment?",
  "How much should I be setting aside for taxes?",
  "Is my business performing better than it was last year?",
];

const STEPS = [
  { num: "01", title: "Review", desc: "A review of your current accounting setup." },
  { num: "02", title: "Discuss", desc: "A conversation about what you actually need." },
  { num: "03", title: "Quote", desc: "Pricing based on the scope of the work — nothing more." },
];

export default function Pricing() {
  return (
    <div>
      <PageHeader
        testid="pricing-header"
        overline="Pricing · 2026"
        lines={["What does bookkeeping", "cost in 2026?"]}
        intro="The cost of bookkeeping depends on the size and complexity of your business."
      />

      <section className="pb-24" data-testid="pricing-intro-section">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <FadeUp>
              <p className="text-lg leading-relaxed text-stone2">
                A business with one bank account and relatively few monthly transactions may only
                need basic reconciliation and financial reporting. A growing company with
                multiple accounts, payroll, sales tax, job costing, accounts receivable, or more
                complex reporting will naturally require more involvement.
              </p>
              <p className="mt-6 text-lg leading-relaxed text-stone2">
                That is why we don't believe bookkeeping is truly a one-size-fits-all service.
              </p>
              <p className="mt-6 border-l-2 border-gold pl-6 font-serif text-2xl italic leading-snug text-pine">
                At Arnold Bookkeeping, pricing is based on what your business actually needs —
                not a predetermined package filled with services you may never use.
              </p>
            </FadeUp>
          </div>
          <div className="lg:col-span-6">
            <FadeUp delay={0.15}>
              <Overline>Factors that affect the cost</Overline>
              <div className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="pricing-factors-grid">
                {FACTORS.map((f, i) => (
                  <div
                    key={f.label}
                    data-testid={`pricing-factor-${i}`}
                    className="flex items-center gap-3.5 rounded-xl border border-hairline bg-white p-4 transition-colors duration-200 hover:border-pine/40"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-parchment text-pine">
                      <f.icon size={18} />
                    </span>
                    <span className="text-sm font-medium leading-snug text-ink">{f.label}</span>
                  </div>
                ))}
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <section className="bg-pine-950 py-24 text-cream lg:py-32" data-testid="pricing-questions-section">
        <div className="mx-auto max-w-7xl px-6">
          <FadeUp>
            <Overline light>Bigger questions</Overline>
          </FadeUp>
          <MaskedLines
            as="h2"
            inView
            lines={["For some businesses, bookkeeping is", "about much bigger questions."]}
            className="mt-5 max-w-4xl font-serif text-3xl font-medium leading-[1.15] tracking-tight sm:text-4xl lg:text-5xl"
          />
          <div className="mt-14 max-w-4xl">
            {QUESTIONS.map((q, i) => (
              <FadeUp key={q} delay={i * 0.06}>
                <div
                  data-testid={`pricing-question-${i}`}
                  className="flex items-baseline gap-6 border-b border-cream/10 py-6"
                >
                  <span className="font-mono text-xs text-gold">{String(i + 1).padStart(2, "0")}</span>
                  <p className="font-serif text-2xl italic leading-snug text-cream/90 sm:text-3xl">{q}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.3}>
            <p className="mt-12 max-w-2xl text-lg leading-relaxed text-cream/70">
              Good bookkeeping should give you confidence in the numbers you use to run your
              business.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="py-24" data-testid="pricing-steps-section">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid gap-6 md:grid-cols-3">
            {STEPS.map((s, i) => (
              <FadeUp key={s.num} delay={i * 0.1} className="h-full">
                <div className="h-full rounded-2xl border border-hairline bg-white p-8">
                  <span className="font-mono text-sm tracking-[0.3em] text-gold">{s.num}</span>
                  <h3 className="mt-4 font-serif text-2xl font-semibold text-ink">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-stone2">{s.desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.3}>
            <div className="mt-12 flex flex-col items-start gap-6 rounded-2xl bg-parchment p-10 sm:flex-row sm:items-center sm:justify-between">
              <p className="max-w-xl font-serif text-2xl italic leading-snug text-ink">
                Arnold Bookkeeping can review your current setup, discuss what you actually need,
                and provide pricing based on the scope of the work.
              </p>
              <Link
                to="/contact"
                data-testid="pricing-steps-cta"
                className="inline-flex shrink-0 items-center gap-2 rounded-md bg-pine px-7 py-4 text-sm font-semibold text-cream transition-colors duration-200 hover:bg-gold hover:text-ink"
              >
                Request a quote <ArrowUpRight size={16} />
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
